import React from 'react';
import TopNav from './TopNav';
import Footer from './Footer';
import useSEO from '../hooks/useSEO';

const MCPPage = () => {
    useSEO({
      title: 'Model Context Protocol (MCP)',
      description: 'Empower your AI with systems thinking. MCP enables AI agents to run simulations and provide predictive insights.'
    });

    return (
        <div className="min-h-screen bg-white">
            <TopNav />

            {/* Main Content */}
            <main className="max-w-3xl mx-auto px-8 py-16 md:py-24">
                    
                {/* Title */}
                <div className="mb-16">
                    <p className="text-sm font-medium text-gray-500 uppercase tracking-wider mb-4">Model Context Protocol</p>
                    <h1 className="text-4xl md:text-5xl font-medium text-gray-900 mb-6 leading-tight tracking-tight">
                        Empowering Your AI with System Thinking
                    </h1>
                    <p className="text-xl text-gray-600 leading-relaxed">
                        MCP is a secure universal translator that allows AI agents to run simulations, analyze system dynamics, and provide predictive insights—the democratization of system thinking.
                    </p>
                </div>

                {/* Content Sections */}
                <div className="space-y-12">
                    
                    {/* Why MCP Matters */}
                    <section>
                        <h2 className="text-2xl font-medium text-gray-900 mb-4">Why MCP matters for leadership</h2>
                        <p className="text-gray-600 mb-6">
                            By adopting MCP, Veydra provides a future-proof foundation for your AI strategy—a portable capability across your entire enterprise suite.
                        </p>
                        <div className="space-y-4">
                            <div className="flex items-start">
                                <span className="text-gray-400 mr-3 mt-1">—</span>
                                <div>
                                    <p className="text-gray-900 font-medium">Accelerated Decision Velocity</p>
                                    <p className="text-gray-600">Shift from "What happened?" to "What if?" in seconds.</p>
                                </div>
                            </div>
                            <div className="flex items-start">
                                <span className="text-gray-400 mr-3 mt-1">—</span>
                                <div>
                                    <p className="text-gray-900 font-medium">Architectural Flexibility</p>
                                    <p className="text-gray-600">Deploy in a way that aligns with your security and IT governance models.</p>
                                </div>
                            </div>
                            <div className="flex items-start">
                                <span className="text-gray-400 mr-3 mt-1">—</span>
                                <div>
                                    <p className="text-gray-900 font-medium">Strategic AI Alignment</p>
                                    <p className="text-gray-600">Ensure your AI is grounded in causal logic of system dynamics, not just generic patterns.</p>
                                </div>
                            </div>
                        </div>
                    </section>

                    {/* Connectivity Options */}
                    <section>
                        <h2 className="text-2xl font-medium text-gray-900 mb-4">Choose your path</h2>
                        <div className="space-y-6">
                            <div className="border border-gray-200 rounded-lg p-6">
                                <p className="text-gray-900 font-medium mb-2">Local Connectivity</p>
                                <p className="text-gray-600 mb-3">
                                    For data scientists and analysts. Run the Veydra connector locally to bridge private databases directly to an AI agent in a zero-trust environment.
                                </p>
                                <code className="text-sm bg-gray-100 px-3 py-1 rounded text-gray-800">
                                    npx @veydra-io/mcp-server
                                </code>
                                <div className="mt-4">
                                    <a href="https://github.com/veydra-io/mcp-server" target="_blank" rel="noopener noreferrer" className="text-sm text-blue-600 hover:underline">
                                        View on GitHub →
                                    </a>
                                </div>
                            </div>
                            <div className="border border-gray-200 rounded-lg p-6">
                                <p className="text-gray-900 font-medium mb-2">Cloud Connectivity</p>
                                <p className="text-gray-600 mb-3">
                                    For VP and Director-level users. Connect AI assistants directly to our Cloud Gateway—no local code or IT installation required.
                                </p>
                                <code className="text-sm bg-gray-100 px-3 py-1 rounded text-gray-800">
                                    https://api.veydra.io/mcp
                                </code>
                                <div className="mt-4">
                                    <a href="https://docs.veydra.io/mcp-integration-guide" target="_blank" rel="noopener noreferrer" className="inline-block bg-gray-900 text-white px-4 py-2 rounded text-sm font-medium hover:bg-gray-800 transition-colors">
                                        Setup Guide
                                    </a>
                                </div>
                            </div>
                        </div>
                    </section>

                    {/* Get Started */}
                    <section>
                        <h2 className="text-2xl font-medium text-gray-900 mb-4">Get started</h2>
                        <p className="text-gray-600 mb-6">
                            Visit our <a href="https://docs.veydra.io/mcp-integration-guide" className="text-blue-600 hover:underline">MCP Integration Guide</a> for the full reference architecture.
                        </p>
                        <p className="text-gray-600">
                            Veydra is leading the shift toward Agentic Workflows—building the future of informed leadership.
                        </p>
                    </section>

                </div>
            </main>

            <Footer />
        </div>
    );
};

export default MCPPage;
